import { useMemo } from 'react';
import type { Stats } from '../types';

export interface SearchFiltersValue {
  search: string;
  state: string;
  nic_code: string;
  status: string;
}

interface SearchFiltersProps {
  value: SearchFiltersValue;
  onChange(next: SearchFiltersValue): void;
  onApply(): void;
  onReset(): void;
  loading?: boolean;
  stats?: Stats;
}

export default function SearchFilters({
  value,
  onChange,
  onApply,
  onReset,
  loading,
  stats,
}: SearchFiltersProps) {
  const isEmpty = useMemo(
    () => !value.state && !value.nic_code && !value.search && !value.status,
    [value]
  );

  const update = (patch: Partial<SearchFiltersValue>) => {
    onChange({ ...value, ...patch });
  };

  const handleReset = () => {
    onChange({ search: '', state: '', nic_code: '', status: '' });
    onReset();
  };

  const statusOptions = stats?.byStatus ?? [];

  return (
    <div className="dashboard-card">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <input
          type="text"
          value={value.search}
          onChange={(e) => update({ search: e.target.value })}
          placeholder="Search name, ID, district…"
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--muted)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        />
        <input
          type="text"
          value={value.state}
          onChange={(e) => update({ state: e.target.value })}
          placeholder="State code (e.g. MH, DL)"
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--muted)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        />
        <input
          type="text"
          value={value.nic_code}
          onChange={(e) => update({ nic_code: e.target.value })}
          placeholder="NIC code"
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--muted)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        />
        <select
          value={value.status}
          onChange={(e) => update({ status: e.target.value })}
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        >
          <option value="">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s.status} value={s.status}>
              {s.status} ({s.count.toLocaleString()})
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onApply}
            disabled={loading}
            className="flex-1 rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-fg)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Applying…' : 'Apply'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={loading || isEmpty}
            className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-4 py-2 text-sm font-medium text-[var(--fg)] transition hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
        </div>
      </div>
      <p className="mt-3 text-xs text-[var(--muted)]">
        Server-side search across millions of raw records may take a moment.
      </p>
    </div>
  );
}
